import { resolveHotelsPoliciesGOL } from "../cbt/api/hotelsPolicies";
import { createResponse } from "../lib/functions";
import { ResponseObject } from "../lib/golObjectTypes/ResponseObject";
import golApiCall from "./golApiCall";

// @ts-ignore - Ignore TypeScript errors for require
const Logger =
  require("../services/Logger").default || require("../services/Logger");

const SEARCH_UNAVAILABLE_MESSAGE_ID = "Search.unavailable" as const;

export type HotelSearchErrorMessageId = typeof SEARCH_UNAVAILABLE_MESSAGE_ID;

interface HotelSearchResponse extends ResponseObject {
  errorMessageId?: HotelSearchErrorMessageId;
}

interface HotelSearchErrorResponse {
  error?: {
    response?: {
      status?: number;
    };
  };
}

export function getHotelSearchErrorMessageId(
  response: HotelSearchErrorResponse
): HotelSearchErrorMessageId | undefined {
  if (!response?.error) {
    return undefined;
  }

  const status = response.error.response?.status;
  return status === undefined || status === 500
    ? SEARCH_UNAVAILABLE_MESSAGE_ID
    : undefined;
}

interface Child {
  Age: number | string;
}

interface SearchedRoom {
  Adults: number;
  Children: number;
  Child?: Child[];
}

interface Room {
  ADT: number;
  CHD: number;
  guests: number;
  childAges?: (number | string)[];
}

const getGolRequestSearchHotelsQuery = ({
  destination,
  departureDate,
  returnDate,
  cbtPassengersForm,
  rooms,
  nationality = "CZ",
  passiveSessionId = null,
  language = "cs",
  country = "CZ",
  requestorClientId = null,
  requestorPassword = null,
}) => {
  const searchedRooms: SearchedRoom[] = [];

  if (rooms && rooms.length > 0) {
    rooms.forEach((room: Room, idx: number) => {
      let assignedCount = 0;
      if (
        cbtPassengersForm &&
        cbtPassengersForm.hotelRoomAssignmentsBookingProcess &&
        cbtPassengersForm.hotelRoomAssignmentsBookingProcess[idx]
      ) {
        assignedCount =
          Object.keys(
            cbtPassengersForm.hotelRoomAssignmentsBookingProcess[idx] || {}
          )?.length || 0;
      }

      const searchedRoom: SearchedRoom = {
        Adults: room.ADT + room.guests + assignedCount,
        Children: room.CHD || 0,
      };

      if (room.childAges && room.CHD > 0) {
        searchedRoom.Child = room.childAges.map((age) => ({ Age: age }));
      }

      searchedRooms.push(searchedRoom);
    });
  }

  return {
    GolApi: {
      ...(passiveSessionId ? { PassiveSessionId: passiveSessionId } : {}),
      ...(requestorClientId && requestorPassword
        ? {
            Authorization: {
              Requestor: {
                ClientId: requestorClientId,
                Password: requestorPassword,
              },
            },
          }
        : {}),
      Settings: {
        Localization: {
          Language: language,
          Country: country,
        },
      },
      RequestDetail: {
        SearchHotelsRequest_3: {
          HotelLocality: {
            Destination: destination,
            CheckInDate: departureDate,
            CheckOutDate: returnDate,
          },
          SearchedRooms: {
            SearchedRoom: searchedRooms,
          },
          Nationality: { $t: nationality },
        },
      },
    },
  };
};

interface CbtParams {
  cbtToken: any;
  userIds: any[];
  guests: any;
  costCenterId: any;
  travelReasonId: any;
  searchFlightsExtendedResp: any;
  departureDate: any;
  returnDate: any;
  customerUsername: any;
  cbtApiUrl: any;
}

function prepareCbtParams({
  hotelsExtendedResp,
  cbtPassengersForm,
  cbtToken,
  departureDate,
  returnDate,
  customerUsername,
  cbtApiUrl,
  guests,
}): CbtParams {
  if (!cbtToken || !cbtPassengersForm) {
    return {
      cbtToken: null,
      userIds: [],
      guests: guests || 0,
      costCenterId: "",
      travelReasonId: "",
      searchFlightsExtendedResp: hotelsExtendedResp || null,
      departureDate: departureDate || "",
      returnDate: returnDate || "",
      customerUsername: customerUsername || "",
      cbtApiUrl: cbtApiUrl || "",
    };
  }

  return {
    cbtToken,
    userIds: cbtPassengersForm.travellers || [],
    guests: cbtPassengersForm.guests || guests,
    costCenterId: cbtPassengersForm.costCenter || "",
    travelReasonId: cbtPassengersForm.travelReason || "",
    searchFlightsExtendedResp: hotelsExtendedResp,
    departureDate: departureDate || "",
    returnDate: returnDate || "",
    customerUsername: customerUsername || "",
    cbtApiUrl: cbtApiUrl || "",
  };
}

export default async ({
  destination,
  departureDate,
  returnDate,
  cbtToken,
  cbtPassengersForm,
  customerUsername,
  cbtApiUrl,
  alternativeCurrency,
  rooms = [],
  nationality = "CZ",
  language = "cs",
  country = "CZ",
  passiveSessionId = null,
  requestorClientId = null,
  requestorPassword = null,
}): Promise<HotelSearchResponse> => {
  const golRequest = getGolRequestSearchHotelsQuery({
    destination,
    departureDate,
    returnDate,
    cbtPassengersForm,
    rooms,
    nationality,
    language,
    country,
    passiveSessionId,
    requestorClientId,
    requestorPassword,
  });

  const hotelsExtendedResp = await golApiCall(golRequest, {
    ...(alternativeCurrency ? { alternativeCurrency } : {}),
  });
  const resp = createResponse(hotelsExtendedResp);
  if (!resp.success) {
    Logger.error(resp, { cause: hotelsExtendedResp?.error });
    return {
      isError: true,
      errorMessageId: getHotelSearchErrorMessageId(hotelsExtendedResp),
    };
  }

  let cbtResolve: any = {};
  const guests = rooms?.reduce((sum, room) => sum + (room.guests || 0), 0) || 0;
  if (resp.data.HotelOffers.HotelOffer.length > 0) {
    cbtResolve = await resolveHotelsPoliciesGOL(
      prepareCbtParams({
        hotelsExtendedResp,
        cbtPassengersForm,
        cbtToken,
        guests,
        departureDate,
        returnDate,
        customerUsername,
        cbtApiUrl,
      })
    );
  }

  const isCbtResolveNotEmpty =
    cbtResolve &&
    typeof cbtResolve === "object" &&
    Object.keys(cbtResolve).length !== 0;

  return {
    ...resp,
    ...(isCbtResolveNotEmpty
      ? {
          travelPolicies: cbtResolve?.data?.TravelPolicies.reduce(
            (acc, pol) => {
              acc[pol.Id] = pol;
              return acc;
            },
            {}
          ),
          travelPoliciesResults: cbtResolve?.data?.TravelPoliciesResults,
        }
      : {}),
  };
};
